import dayjs from 'dayjs';

import { BadgeColor } from 'types';
import type { Reader, ReaderTone } from 'store/api/clover/reader-status';

// The dot colours are the app's own badge dot tokens (theme.config.badge.*.dot,
// light and dark themes): green / amber / red / grey, as plan §1.
export const READER_TONE_COLOR: Record<ReaderTone, BadgeColor> = {
  bad: BadgeColor.Red,
  ok: BadgeColor.Green,
  muted: BadgeColor.Grey,
  warn: BadgeColor.Yellow,
};

// "Last seen": "just now", "2 min ago", "today 9:14 AM", "yesterday 6:02 PM",
// then "Oct 18, 6:02 PM". Empty when the reader has never answered.
export const lastSeenText = (
  value?: string | null,
  now: dayjs.ConfigType = new Date()
): string => {
  const at = value ? dayjs(value) : null;
  if (!at || !at.isValid()) {
    return '';
  }
  const current = dayjs(now);
  const minutes = Math.max(0, current.diff(at, 'minute'));
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  if (at.isSame(current, 'day')) {
    return `today ${at.format('h:mm A')}`;
  }
  if (at.isSame(current.subtract(1, 'day'), 'day')) {
    return `yesterday ${at.format('h:mm A')}`;
  }
  return at.format(
    at.isSame(current, 'year') ? 'MMM D, h:mm A' : 'MMM D, YYYY'
  );
};

export const findReader = (
  readers: Reader[] | undefined,
  deviceId?: number | string | null
) =>
  deviceId === undefined || deviceId === null || deviceId === ''
    ? undefined
    : readers?.find((reader) => reader.deviceId === Number(deviceId));

// Reader hold (owner 24 Oct): another rep's cart or card sale has this reader
// (the server works it out for the signed-in rep). Red, and it can't be picked.
export const isHeldByOther = (
  reader?: Pick<Reader, 'status' | 'heldByYou'> | null
) =>
  !!reader &&
  (reader.status === 'in_use' ||
    (['in_sale', 'waiting'].includes(reader.status) && !reader.heldByYou));

// A list older than 15 s (live channel and reads failing): never a green dot.
export const RECONNECTING_WORD = 'Reconnecting…';
export const asStale = <T extends Pick<Reader, 'tone' | 'word'>>(reader: T): T => ({
  ...reader,
  tone: 'muted',
  word: RECONNECTING_WORD,
});
